import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Check, MessageCircle, 
  X, Send, Loader2, AlertCircle, Sparkles
} from 'lucide-react';

const QuestionnairePage = () => {
  const { sectorId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [questionnaire, setQuestionnaire] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Chat assistant
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Load questionnaire and start session
  useEffect(() => {
    const init = async () => {
      try {
        // Fetch questionnaire
        const qRes = await fetch(`/api/questionnaire/${sectorId}`);
        const qData = await qRes.json();
        
        if (!qData.success) {
          throw new Error('Questionnaire non trouvé');
        }
        
        setQuestionnaire(qData.data);
        
        // Start session
        const sRes = await fetch('/api/sessions/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sectorId })
        });
        const sData = await sRes.json();
        
        if (sData.success) {
          setSessionId(sData.data.sessionId);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    init();
  }, [sectorId]);

  // Current question helpers
  const currentSection = questionnaire?.sections[currentSectionIndex];
  const currentQuestion = currentSection?.questions[currentQuestionIndex];
  const totalQuestions = questionnaire?.totalQuestions || 0;
  
  const questionNumber = questionnaire?.sections
    .slice(0, currentSectionIndex)
    .reduce((sum, s) => sum + s.questions.length, 0) + currentQuestionIndex + 1;
  
  const progress = (questionNumber / totalQuestions) * 100;

  // Check if question should be shown (conditions)
  const shouldShowQuestion = useCallback((question) => {
    if (!question.condition) return true;
    
    const { questionId, notEquals, equals } = question.condition;
    const answer = responses[questionId]?.answer;
    
    if (notEquals) return answer !== notEquals;
    if (equals) return answer === equals;
    return true;
  }, [responses]);

  // Save response
  const saveResponse = async (questionId, answer) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { answer, answeredAt: new Date().toISOString() }
    }));
    
    // Persist to server
    if (sessionId) {
      await fetch(`/api/sessions/${sessionId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId, answer })
      });
    }
  };

  // Navigation
  const goNext = () => {
    let nextQuestionIndex = currentQuestionIndex + 1;
    let nextSectionIndex = currentSectionIndex;
    
    // Skip questions that don't meet conditions
    while (true) {
      if (nextQuestionIndex >= currentSection.questions.length) {
        nextSectionIndex++;
        nextQuestionIndex = 0;
        
        if (nextSectionIndex >= questionnaire.sections.length) {
          // End of questionnaire
          handleSubmit();
          return;
        }
      }
      
      const nextQuestion = questionnaire.sections[nextSectionIndex].questions[nextQuestionIndex];
      if (shouldShowQuestion(nextQuestion)) {
        break;
      }
      nextQuestionIndex++;
    }
    
    setCurrentSectionIndex(nextSectionIndex);
    setCurrentQuestionIndex(nextQuestionIndex);
  };

  const goPrevious = () => {
    let prevQuestionIndex = currentQuestionIndex - 1;
    let prevSectionIndex = currentSectionIndex;
    
    while (true) {
      if (prevQuestionIndex < 0) {
        prevSectionIndex--;
        if (prevSectionIndex < 0) return;
        prevQuestionIndex = questionnaire.sections[prevSectionIndex].questions.length - 1;
      }
      
      const prevQuestion = questionnaire.sections[prevSectionIndex].questions[prevQuestionIndex];
      if (shouldShowQuestion(prevQuestion)) {
        break;
      }
      prevQuestionIndex--;
    }
    
    setCurrentSectionIndex(prevSectionIndex);
    setCurrentQuestionIndex(prevQuestionIndex);
  };

  // Validation de toutes les questions required
  const validateAllRequired = () => {
    const allQuestions = questionnaire.sections.flatMap(s => s.questions);
    const missing = allQuestions.filter(q =>
      q.required && shouldShowQuestion(q) && !responses[q.id]?.answer
    );
    return missing;
  };

  // Submit questionnaire
  const handleSubmit = async () => {
    const missing = validateAllRequired();
    if (missing.length > 0) {
      setError(`${missing.length} question(s) obligatoire(s) non répondue(s)`);
      // Naviguer vers la première question manquante
      const firstMissing = missing[0];
      for (let si = 0; si < questionnaire.sections.length; si++) {
        const qi = questionnaire.sections[si].questions.findIndex(q => q.id === firstMissing.id);
        if (qi !== -1) {
          setCurrentSectionIndex(si);
          setCurrentQuestionIndex(qi);
          break;
        }
      }
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`/api/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        navigate(`/results/${sessionId}`);
      } else {
        throw new Error(data.error || 'Erreur lors de la soumission');
      }
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  // Chat assistant
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          context: {
            currentSection: currentSection?.title,
            currentQuestion: currentQuestion?.label,
            sector: sectorId
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.data.message,
          provider: data.data.provider
        }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, je rencontre un problème technique. Réessayez dans un instant.',
        error: true
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Check if current question has answer
  const hasAnswer = currentQuestion && responses[currentQuestion.id]?.answer;
  const isRequired = currentQuestion?.required && !hasAnswer;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du questionnaire...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Submitting state
  if (submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-6">
            <div className="w-20 h-20 border-4 border-primary-200 rounded-full mx-auto"></div>
            <div className="w-20 h-20 border-4 border-primary-500 border-t-transparent rounded-full animate-spin absolute top-0 left-1/2 -translate-x-1/2"></div>
            <Sparkles className="w-8 h-8 text-primary-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Analyse en cours...</h2>
          <p className="text-gray-600">
            Notre IA analyse vos réponses pour générer votre diagnostic personnalisé
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Quitter</span>
            </button>
            <span className="text-sm text-gray-500">
              {questionnaire?.sectorName}
            </span>
          </div>
          
          {/* Progress */}
          <div className="progress-bar">
            <motion.div 
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>Question {questionNumber} / {totalQuestions}</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentSectionIndex}-${currentQuestionIndex}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Section title */}
            {currentQuestionIndex === 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {currentSection.title}
                </h2>
                {currentSection.description && (
                  <p className="text-gray-600">{currentSection.description}</p>
                )}
              </div>
            )}

            {/* Question */}
            <div className="card mb-8">
              <label className="block text-lg font-medium text-gray-900 mb-6">
                {currentQuestion.label}
                {currentQuestion.required && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>

              {/* Question types */}
              <QuestionInput
                question={currentQuestion}
                value={responses[currentQuestion.id]?.answer}
                onChange={(value) => saveResponse(currentQuestion.id, value)}
              />
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={goPrevious}
            disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
            className="btn btn-ghost flex items-center gap-2 disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4" />
            Précédent
          </button>
          
          <button
            onClick={goNext}
            disabled={isRequired}
            className="btn btn-primary flex items-center gap-2"
          >
            {questionNumber === totalQuestions ? (
              <>
                Terminer
                <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Suivant
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </main>

      {/* Chat Assistant Button */}
      <button
        onClick={() => setChatOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary-500 text-white rounded-full shadow-lg hover:bg-primary-700 transition-all hover:scale-110 flex items-center justify-center z-50"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
          >
            {/* Chat header */}
            <div className="bg-primary-500 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">Assistant IA</span>
              </div>
              <button onClick={() => setChatOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Chat messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Besoin d'aide pour comprendre une question ?</p>
                  <p className="text-sm mt-1">Je suis là pour vous aider !</p>
                </div>
              )}
              
              {chatMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`chat-bubble ${msg.role}`}
                >
                  {msg.content}
                  {msg.provider && (
                    <div className="text-xs opacity-60 mt-1">
                      via {msg.provider}
                    </div>
                  )}
                </div>
              ))}
              
              {chatLoading && (
                <div className="chat-bubble assistant flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Réflexion...</span>
                </div>
              )}
            </div>
            
            {/* Chat input */}
            <div className="border-t border-gray-200 p-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Posez votre question..."
                  className="input flex-1 py-2"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || chatLoading}
                  className="btn btn-primary px-3 py-2"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Question Input Component
const QuestionInput = ({ question, value, onChange }) => {
  switch (question.type) {
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="input"
        />
      );
    
    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          maxLength={question.maxLength}
          rows={4}
          className="input resize-none"
        />
      );
    
    case 'single':
      return (
        <div className="space-y-3">
          {question.options.map((option) => (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              className={`option-card w-full text-left ${value === option.value ? 'selected' : ''}`}
            >
              <div className={`option-radio ${value === option.value ? 'selected' : ''}`} />
              <div>
                <div className="font-medium text-gray-900">{option.label}</div>
              </div>
            </button>
          ))}
        </div>
      );
    
    case 'multiple':
      const selectedValues = value || [];
      const maxSelect = question.maxSelect;
      
      const toggleOption = (optValue) => {
        if (selectedValues.includes(optValue)) {
          onChange(selectedValues.filter(v => v !== optValue));
        } else if (!maxSelect || selectedValues.length < maxSelect) {
          onChange([...selectedValues, optValue]);
        }
      };
      
      return (
        <div className="space-y-3">
          {maxSelect && (
            <p className="text-sm text-gray-500 mb-2">
              Sélectionnez jusqu'à {maxSelect} réponses
            </p>
          )}
          {question.options.map((option) => (
            <button
              key={option.value}
              onClick={() => toggleOption(option.value)}
              className={`option-card w-full text-left ${selectedValues.includes(option.value) ? 'selected' : ''}`}
            >
              <div className={`option-checkbox ${selectedValues.includes(option.value) ? 'selected' : ''}`}>
                {selectedValues.includes(option.value) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">{option.label}</div>
              </div>
            </button>
          ))}
        </div>
      );
    
    case 'scale':
      return (
        <div>
          <div className="scale-container">
            {Array.from({ length: question.max - question.min + 1 }, (_, i) => {
              const scaleValue = question.min + i;
              return (
                <button
                  key={scaleValue}
                  onClick={() => onChange(scaleValue)}
                  className={`scale-option ${value === scaleValue ? 'selected' : ''}`}
                >
                  {scaleValue}
                </button>
              );
            })}
          </div>
          {question.labels && (
            <div className="mt-4 space-y-2">
              {Object.entries(question.labels).map(([key, label]) => (
                <div key={key} className="flex gap-2 text-sm">
                  <span className="font-medium text-gray-700 w-6">{key}.</span>
                  <span className="text-gray-600">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    
    case 'ranking':
      const rankedValues = value || [];
      
      const moveUp = (index) => {
        if (index === 0) return;
        const newRanking = [...rankedValues];
        [newRanking[index - 1], newRanking[index]] = [newRanking[index], newRanking[index - 1]];
        onChange(newRanking);
      };
      
      const moveDown = (index) => {
        if (index === rankedValues.length - 1) return;
        const newRanking = [...rankedValues];
        [newRanking[index], newRanking[index + 1]] = [newRanking[index + 1], newRanking[index]];
        onChange(newRanking);
      };
      
      const addToRanking = (optValue) => {
        if (!rankedValues.includes(optValue)) {
          onChange([...rankedValues, optValue]);
        }
      };
      
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Cliquez sur les options pour les classer par ordre de priorité
          </p>
          
          {/* Ranked items */}
          {rankedValues.length > 0 && (
            <div className="space-y-2 p-4 bg-primary-50 rounded-lg">
              <p className="text-sm font-medium text-primary-700 mb-2">Votre classement :</p>
              {rankedValues.map((val, index) => {
                const option = question.options.find(o => o.value === val);
                return (
                  <div key={val} className="flex items-center gap-2 bg-white p-2 rounded-lg">
                    <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </span>
                    <span className="flex-1">{option?.label}</span>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => moveUp(index)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <button 
                        onClick={() => moveDown(index)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        disabled={index === rankedValues.length - 1}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Available options */}
          <div className="space-y-2">
            {question.options
              .filter(o => !rankedValues.includes(o.value))
              .map((option) => (
                <button
                  key={option.value}
                  onClick={() => addToRanking(option.value)}
                  className="option-card w-full text-left"
                >
                  <div className="font-medium text-gray-900">{option.label}</div>
                </button>
              ))}
          </div>
        </div>
      );
    
    case 'contact':
      const contactValues = value || {};
      
      return (
        <div className="space-y-4">
          {question.fields.map((field) => (
            <div key={field.id}>
              <label className="label">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type={field.type}
                value={contactValues[field.id] || ''}
                onChange={(e) => onChange({ ...contactValues, [field.id]: e.target.value })}
                className="input"
              />
            </div>
          ))}
        </div>
      );
    
    default:
      return <p className="text-gray-500">Type de question non supporté</p>;
  }
};

export default QuestionnairePage;
